import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MediaRecorder } from "@/components/MediaRecorder";
import { Sparkles, BookOpen, Mic, Video, Save, XCircle, PaperclipIcon, FileImage, File, MoreVertical, Menu, Share2 } from "lucide-react";
import SimpleFileUploader from "@/components/journal/SimpleFileUploader";
import AttachmentList from "@/components/journal/AttachmentList";
import { JournalAttachment } from "@/types/journal";
import { useJournalAttachments } from "@/hooks/useJournalAttachments";
import { useNavigate } from "react-router-dom";
import SimplePhotoUploader from "./SimplePhotoUploader";
import { getEmotionColor } from "./EmotionChart";
import { EMOTION_TYPES } from "@/types/emotions";
import { useQueryClient } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

interface JournalEntryFormProps {
  onEntryCreated: () => void;
}

const JournalEntryForm: React.FC<JournalEntryFormProps> = ({ onEntryCreated }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [content, setContent] = useState("");
  const [mood, setMood] = useState("");
  const [emotionType, setEmotionType] = useState<string>("");
  const [emotionIntensity, setEmotionIntensity] = useState<number>(5);
  const [mediaUrl, setMediaUrl] = useState<string>("");
  const [mediaType, setMediaType] = useState<'audio' | 'video' | null>(null);
  const [activeEntryId, setActiveEntryId] = useState<string | null>(null);
  const [speechError, setSpeechError] = useState<string | null>(null);
  const [shareableWithTherapist, setShareableWithTherapist] = useState(true);
  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();
  const { uploadAttachment, attachments, removeAttachment } = useJournalAttachments(activeEntryId);
  const [hasTherapist, setHasTherapist] = useState(false);
  const [audioDialogOpen, setAudioDialogOpen] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);

  // Add effect to handle speech recognition errors
  useEffect(() => {
    if (!browserSupportsSpeechRecognition) {
      setSpeechError("Speech recognition is not supported in your browser. Please use Chrome, Edge, or another supported browser.");
    }
  }, [browserSupportsSpeechRecognition]);

  useEffect(() => {
    const checkTherapistConnection = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return setHasTherapist(false);
      const { data: relationships, error } = await supabase
        .from('client_therapist_relationships')
        .select('*')
        .eq('client_id', user.id)
        .eq('status', 'active')
        .limit(1);
      if (error) return setHasTherapist(false);
      setHasTherapist(relationships && relationships.length > 0);
    };
    checkTherapistConnection();
  }, []);

  const handleSpeechRecognition = async () => {
    try {
      if (listening) {
        SpeechRecognition.stopListening();
      } else {
        // Check if we're in a secure context
        if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
          setSpeechError("Speech recognition requires a secure connection (HTTPS). Please use a secure connection to enable this feature.");
          return;
        }

        // Request microphone permission explicitly
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          resetTranscript();
          SpeechRecognition.startListening({ continuous: true });
          setSpeechError(null);
        } catch (error) {
          if (error.name === 'NotAllowedError') {
            setSpeechError("Microphone access was denied. Please enable microphone access in your browser settings.");
          } else {
            setSpeechError("Failed to access microphone. Please check your browser settings.");
          }
        }
      }
    } catch (error) {
      setSpeechError("Failed to start speech recognition. Please try again.");
      console.error('Speech recognition error:', error);
    }
  };

  const openRecordingDialog = (type: 'audio' | 'video') => {
    if (!emotionType) {
      toast.error("Please select your emotion before recording");
      return;
    }
    if (type === 'audio') setAudioDialogOpen(true);
    else setVideoDialogOpen(true);
  };

  const handleMediaRecordingComplete = (url: string, type: 'audio' | 'video') => {
    setMediaUrl(url);
    setMediaType(type);
    setAudioDialogOpen(false);
    setVideoDialogOpen(false);
    toast.success(`${type === 'audio' ? 'Audio' : 'Video'} recording saved`);
  };

  const handlePhotoAdded = async (file: File) => {
    if (!mood.trim()) {
      toast.error("Please enter how you are feeling before adding photos");
      return;
    }
    if (!emotionType) {
      toast.error("Please select your emotion before adding photos");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in to create journal entries");
        navigate('/auth');
        return;
      }

      let entryId = activeEntryId;

      // If no entry ID exists, save the entry first
      if (!entryId) {
        console.log("No active entry ID, creating new entry first...");
        const { data: entryData, error } = await supabase.from('journal_entries').insert({
          content,
          mood,
          emotion_type: emotionType,
          emotion_intensity: emotionIntensity,
          user_id: user.id,
          media_url: mediaUrl || null,
          media_type: mediaType || null
        }).select('*').single();
        
        if (error) {
          console.error("Error creating entry:", error);
          throw error;
        }

        console.log("Created new entry:", entryData);
        entryId = entryData.id;
        setActiveEntryId(entryData.id);
        
        // Invalidate both queries to ensure the new entry is included
        await queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
        await queryClient.invalidateQueries({ queryKey: ["journal-attachments"] });
        
        // Wait for the queries to be refetched
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      console.log("Uploading photo with entry ID:", entryId);
      // Now that we have an entry ID, upload the photo
      const attachment = await uploadAttachment(file, entryId);
      if (attachment) {
        // Invalidate the attachments query to trigger a refetch
        await queryClient.invalidateQueries({ queryKey: ["journal-attachments"] });
        toast.success("Photo uploaded successfully");
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error("Failed to save journal entry");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mood.trim()) {
      toast.error("Please enter how you are feeling");
      return;
    }
    if (!emotionType) {
      toast.error("Please select your emotion");
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("You must be logged in to create journal entries");
      navigate('/auth');
      return;
    }
    
    try {
      let entryData, error;
      if (activeEntryId) {
        // Update the existing entry
        const { data, error: updateError } = await supabase
          .from('journal_entries')
          .update({
            content,
            mood,
            emotion_type: emotionType,
            emotion_intensity: emotionIntensity,
            user_id: user.id,
            media_url: mediaUrl || null,
            media_type: mediaType || null,
            shareable_with_therapist: shareableWithTherapist
          })
          .eq('id', activeEntryId)
          .select('*')
          .single();
        entryData = data;
        error = updateError;
      } else {
        // Insert a new entry
        const { data, error: insertError } = await supabase
          .from('journal_entries')
          .insert({
            content,
            mood,
            emotion_type: emotionType,
            emotion_intensity: emotionIntensity,
            user_id: user.id,
            media_url: mediaUrl || null,
            media_type: mediaType || null,
            shareable_with_therapist: shareableWithTherapist
          })
          .select('*')
          .single();
        entryData = data;
        error = insertError;
        setActiveEntryId(entryData.id);
      }
      
      if (error) throw error;

      // Clear form state
      setContent("");
      setMood("");
      setEmotionType("");
      setEmotionIntensity(5);
      setMediaUrl("");
      setMediaType(null);
      setShareableWithTherapist(true);
      
      // Call onEntryCreated callback
      onEntryCreated();
      
      // Show success message
      toast.success("Journal entry saved successfully!");
      
      // Invalidate queries to ensure the new entry is included
      await queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
      await queryClient.invalidateQueries({ queryKey: ["journal-attachments"] });
      
      // Wait a brief moment to ensure state updates are processed
      setTimeout(() => {
        // Navigate to All Journal Entries page with the new entry's ID as hash
        navigate(`/journal/all#${entryData.id}`);
      }, 500);
      
      // Keep the activeEntryId for a moment to ensure attachments are properly saved
      setTimeout(() => {
        setActiveEntryId(null);
      }, 1000);
    } catch (error) {
      toast.error("Failed to save journal entry");
      console.error(error);
    }
  };

  // Add effect to update content when transcript changes (only if listening)
  useEffect(() => {
    if (listening) {
      setContent(transcript);
    }
  }, [transcript, listening]);

  return (
    <Card className="bg-white backdrop-blur-sm border border-[#CCCCCC]">
      <CardHeader className="bg-gradient-to-r from-[#E8F0F8] to-white rounded-t-lg">
        <CardTitle className="text-[#333B44]">New Journal Entry</CardTitle>
        <CardDescription>Record your thoughts and feelings</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-4">
            {/* Stack fields vertically on mobile, side by side on desktop */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* First field - Mood */}
              <div className="w-full sm:w-1/2 space-y-2">
                <label className="text-sm font-medium">How are you feeling?</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md placeholder:text-muted-foreground"
                  placeholder="Describe your mood"
                  value={mood}
                  onChange={(e) => setMood(e.target.value)}
                />
              </div>

              {/* Second field - Emotion Type */}
              <div className="w-full sm:w-1/2 space-y-2">
                <label className="text-sm font-medium">Select your emotion</label>
                <Select value={emotionType} onValueChange={setEmotionType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an emotion" className="placeholder:text-muted-foreground" />
                  </SelectTrigger>
                  <SelectContent>
                    {EMOTION_TYPES.map((emotion) => (
                      <SelectItem key={emotion} value={emotion}>
                        {emotion}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description field */}
            <div className="flex flex-col space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                Description
                {browserSupportsSpeechRecognition && (
                  <button
                    type="button"
                    onClick={handleSpeechRecognition}
                    className={`ml-2 px-2 py-1 rounded-full border transition-colors duration-200 ${listening ? 'bg-[#3BAFDA] text-white border-[#333B44]' : 'bg-white text-[#333B44] border-[#3BAFDA] hover:bg-[#E8F0F8]'}`}
                    aria-label={listening ? 'Stop voice input' : 'Start voice input'}
                  >
                    {listening ? '🎤 Stop' : '🎤 Speak'}
                  </button>
                )}
              </label>
              <Textarea
                placeholder="Write your thoughts here..."
                className="min-h-[200px] placeholder:text-muted-foreground"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
              {listening && (
                <span className="text-xs text-[#3BAFDA] mt-1">Listening... Speak now.</span>
              )}
              {speechError && (
                <span className="text-xs text-red-500 mt-1">{speechError}</span>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2 text-[#333B44]">
              Emotion Intensity: {emotionIntensity}
            </label>
            <Slider 
              value={[emotionIntensity]} 
              onValueChange={value => setEmotionIntensity(value[0])} 
              min={1} 
              max={10} 
              step={1} 
              className="w-full" 
            />
          </div>
          
          <div className="flex flex-col items-center space-y-3 p-4 border border-[#CCCCCC] rounded-lg bg-white">
            <div className="flex items-center space-x-3">
              <Switch
                id="share-toggle"
                checked={hasTherapist && shareableWithTherapist}
                onCheckedChange={(checked) => {
                  if (hasTherapist) setShareableWithTherapist(checked);
                }}
                disabled={!hasTherapist}
                className={`${hasTherapist ? (shareableWithTherapist ? 'bg-green-600' : 'bg-red-600') : 'bg-gray-300'}`}
              />
              <Label htmlFor="share-toggle" className="flex items-center space-x-2 text-lg font-medium">
                <Share2 className="h-5 w-5" />
                <span>SHARE WITH MY THERAPIST</span>
              </Label>
            </div>
            <p className={`mt-2 text-sm ${hasTherapist ? (shareableWithTherapist ? 'text-green-600' : 'text-red-600') : 'text-gray-400'}`}>
              {hasTherapist
                ? (shareableWithTherapist
                    ? 'Your therapist will be able to see this entry and provide feedback'
                    : 'Your therapist will not see this entry')
                : 'Connect to a therapist to enable sharing.'}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Desktop buttons - hidden on mobile */}
            <div className="hidden sm:flex gap-4 w-full">
              <Button 
                type="button" 
                variant="outline" 
                className="border-[#CCCCCC] hover:bg-[#E8F0F8] transition-all duration-300 flex-1"
                onClick={() => openRecordingDialog('audio')}
              >
                <Mic className="mr-2 h-4 w-4" />
                Record Audio
              </Button>
              <Dialog open={audioDialogOpen} onOpenChange={setAudioDialogOpen}>
                <DialogContent className="bg-white backdrop-blur-lg border border-[#CCCCCC]">
                  <DialogHeader>
                    <DialogTitle className="text-[#333B44]">Record Audio Journal Entry</DialogTitle>
                  </DialogHeader>
                  <MediaRecorder onRecordingComplete={handleMediaRecordingComplete} mediaType="audio" />
                </DialogContent>
              </Dialog>
              <Button 
                type="button" 
                variant="outline" 
                className="border-[#CCCCCC] hover:bg-[#E8F0F8] transition-all duration-300 flex-1"
                onClick={() => openRecordingDialog('video')}
              >
                <Video className="mr-2 h-4 w-4" />
                Record Video
              </Button>
              <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
                <DialogContent className="sm:max-w-[600px] bg-white backdrop-blur-lg border border-[#CCCCCC]">
                  <DialogHeader>
                    <DialogTitle className="text-[#333B44]">Record Video Journal Entry</DialogTitle>
                  </DialogHeader>
                  <MediaRecorder onRecordingComplete={handleMediaRecordingComplete} mediaType="video" />
                </DialogContent>
              </Dialog>
              <SimplePhotoUploader onFileAdded={handlePhotoAdded} />
            </div>

            {/* Mobile dropdown - hidden on desktop */}
            <div className="sm:hidden w-full">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full border-[#CCCCCC] hover:bg-[#E8F0F8]"
                  >
                    <MoreVertical className="mr-2 h-4 w-4" />
                    Add Media
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  <DropdownMenuItem onClick={() => openRecordingDialog('audio')}>
                    <Mic className="mr-2 h-4 w-4" />
                    Record Audio
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openRecordingDialog('video')}>
                    <Video className="mr-2 h-4 w-4" />
                    Record Video
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <SimplePhotoUploader onFileAdded={handlePhotoAdded} />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          
          {mediaUrl && (
            <div className="space-y-2 p-4 border border-[#CCCCCC] rounded-lg bg-white backdrop-blur-sm">
              <p className="text-sm font-medium text-[#333B44] flex items-center">
                {mediaType === 'audio' ? 
                  <><Mic className="mr-2 h-4 w-4" /> Audio Recording Added</> : 
                  <><Video className="mr-2 h-4 w-4" /> Video Recording Added</>
                }
              </p>
              {mediaType === 'audio' ? 
                <div className="flex justify-center">
                  <audio controls src={mediaUrl} className="w-full" />
                </div>
              : 
                <div className="flex justify-center">
                  <video controls src={mediaUrl} className="w-full max-w-[350px] max-h-[300px] h-auto rounded-lg object-contain" />
                </div>
              }
              <Button 
                type="button" 
                variant="destructive" 
                onClick={() => {
                  setMediaUrl('');
                  setMediaType(null);
                }} 
                className="mt-2"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Remove Recording
              </Button>
            </div>
          )}
          
          {/* Show attachments if there are any */}
          {attachments && attachments.length > 0 && (
            <div className="mt-4 p-3 border border-[#CCCCCC] rounded-lg bg-white">
              <AttachmentList 
                attachments={attachments}
                onDelete={removeAttachment}
              />
            </div>
          )}
          
          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-[#3BAFDA] to-[#333B44] hover:from-[#333B44] hover:to-[#333B44] text-white transition-all duration-300"
          >
            <Save className="mr-2 h-4 w-4" />
            Save Entry
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default JournalEntryForm;
